import React, { useState } from "react";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaPhoneAlt, FaEnvelope, FaMapMarkerAlt, FaSpinner, FaPaperPlane } from 'react-icons/fa';
import { submitContactForm } from './api';
import useSEO from './useSEO';

const ContactUs = () => {
  useSEO({
    title: "Contact Core Spectators | Software Engineering & E-Commerce Consultations",
    description: "Connect directly with Core Spectators software architects. Request an architectural review and quote for custom Shopify 2.0 stores, Flutter mobile apps, SaaS MVPs, or AI automations.",
    keywords: "contact Core Spectators, hire shopify plus developers, hire flutter developers, saas mvp consultation, custom software engineering estimate, ai workflow automation quote",
    canonical: "https://corespectators.com/Contact",
    schema: {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "Contact Core Spectators Engineering Agency",
      "url": "https://corespectators.com/Contact",
      "description": "Schedule a technical consultation with Core Spectators for custom Shopify storefronts, Flutter mobile apps, SaaS MVPs, and AI workflow automations.",
      "mainEntity": {
        "@type": "Organization",
        "name": "Core Spectators",
        "url": "https://corespectators.com",
        "email": "contact@corespectators.com",
        "telephone": ["+1-332-378-8142", "+92-333-5136060"],
        "contactPoint": [
          {
            "@type": "ContactPoint",
            "telephone": "+1-332-378-8142",
            "contactType": "US Client Desk",
            "availableLanguage": ["English"]
          },
          {
            "@type": "ContactPoint",
            "telephone": "+92-333-5136060",
            "contactType": "Global Engineering Desk & WhatsApp",
            "availableLanguage": ["English", "Urdu"]
          }
        ],
        "address": [
          {
            "@type": "PostalAddress",
            "streetAddress": "128-B Spring Garden, NY",
            "addressLocality": "New York",
            "addressRegion": "NY",
            "addressCountry": "US"
          },
          {
            "@type": "PostalAddress",
            "streetAddress": "Blue Area",
            "addressLocality": "Islamabad",
            "addressCountry": "PK"
          }
        ]
      }
    }
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      toast.error("Please fill in all required fields (Name, Email, and Message).");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) {
      toast.error("Please enter a valid email address.");
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await submitContactForm(formData);
      if (res.success) {
        toast.success(res.message || "Message sent successfully! We will get back to you soon.");
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        toast.error(res.error || "Failed to send message. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting contact form:", error);
      const errMsg = error.response?.data?.error || "Failed to send message. Please verify your connection.";
      toast.error(errMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="bg-white">
        {/* Header Section */}
        <section className="text-center py-12 px-4 max-w-6xl mx-auto">
          <span className="text-red-600 font-semibold uppercase tracking-wider text-sm border-b-2 border-red-600 pb-1">
            Get in touch
          </span>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mt-4">
            Let's Build Something <span className="text-red-600">Great Together</span>
          </h1>
          <p className="mt-4 text-gray-600 max-w-2xl mx-auto text-sm sm:text-base">
            Have a project in mind or need expert software engineering, web design, or digital branding? Contact us and our team will get back to you within 24 hours.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
            {/* Phone Numbers: US & Pakistan */}
            <ContactMethod 
              icon={<FaPhoneAlt className="text-2xl mx-auto" />} 
              title="Call Us" 
            >
              <div className="w-full space-y-2 mt-2">
                <div className="p-2.5 rounded-lg bg-gray-50 border border-gray-100 hover:border-red-200 transition">
                  <a href="tel:+13323788142" className="text-gray-900 font-semibold text-sm hover:text-red-600 transition block">
                    +1-332-378-8142
                  </a>
                  <span className="text-xs text-gray-500 block mt-0.5">United States Client Desk</span>
                </div>
                <div className="p-2.5 rounded-lg bg-gray-50 border border-gray-100 hover:border-red-200 transition">
                  <a href="tel:+923335136060" className="text-gray-900 font-semibold text-sm hover:text-red-600 transition block">
                    +92 333 5136060
                  </a>
                  <span className="text-xs text-gray-500 block mt-0.5">Pakistan Office &amp; WhatsApp</span>
                </div>
              </div>
            </ContactMethod>

            {/* Email Addresses */}
            <ContactMethod 
              icon={<FaEnvelope className="text-2xl mx-auto" />} 
              title="Email Us" 
            >
              <div className="w-full space-y-2 mt-2">
                <div className="p-2.5 rounded-lg bg-gray-50 border border-gray-100 hover:border-red-200 transition">
                  <a href="mailto:contact@corespectators.com" className="text-gray-900 font-semibold text-sm hover:text-red-600 transition block">
                    contact@corespectators.com
                  </a>
                  <span className="text-xs text-gray-500 block mt-0.5">Architecture &amp; Project Scope</span>
                </div>
                <div className="p-2.5 rounded-lg bg-gray-50 border border-gray-100 hover:border-red-200 transition">
                  <a href="mailto:info@corespectators.com" className="text-gray-900 font-semibold text-sm hover:text-red-600 transition block">
                    info@corespectators.com
                  </a>
                  <span className="text-xs text-gray-500 block mt-0.5">General Inquiries &amp; Support</span>
                </div>
              </div>
            </ContactMethod>

            {/* Office Locations: US & Pakistan */}
            <ContactMethod 
              icon={<FaMapMarkerAlt className="text-2xl mx-auto" />} 
              title="Visit Us" 
            >
              <div className="w-full space-y-2 mt-2">
                <div className="p-2.5 rounded-lg bg-gray-50 border border-gray-100 hover:border-red-200 transition">
                  <span className="text-gray-900 font-semibold text-sm block">128-B Spring Garden, NY</span>
                  <span className="text-xs text-gray-500 block mt-0.5">New York, United States</span>
                </div>
                <div className="p-2.5 rounded-lg bg-gray-50 border border-gray-100 hover:border-red-200 transition">
                  <span className="text-gray-900 font-semibold text-sm block">Blue Area, Islamabad</span>
                  <span className="text-xs text-gray-500 block mt-0.5">Islamabad, Pakistan</span>
                </div>
              </div>
            </ContactMethod>
          </div>
        </section>

        {/* Form & Graphic Section */}
        <section className="bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 border-t border-b border-gray-200">
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center gap-10">
            <div className="w-full md:w-1/2 flex justify-center">
              <img 
                src="/images/cont.svg" 
                alt="Contact Core Spectators" 
                className="w-full max-h-[420px] object-contain drop-shadow-md"
              />
            </div>

            <div className="w-full md:w-1/2 bg-white p-6 sm:p-8 rounded-2xl shadow-lg border border-gray-100">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Send Us A Message</h2>
              <p className="text-gray-600 text-sm mb-6">Fill in the details below and we'll promptly respond.</p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-gray-700 font-semibold text-sm mb-1">
                    Your Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="e.g. John Doe"
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 transition text-gray-900"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="email" className="block text-gray-700 font-semibold text-sm mb-1">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@example.com"
                      required
                      className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 transition text-gray-900"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-gray-700 font-semibold text-sm mb-1">
                      Phone Number (Optional)
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+92 333 1234567"
                      className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 transition text-gray-900"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-gray-700 font-semibold text-sm mb-1">
                    Your Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    rows="4"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your project requirements, scope, or questions..."
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 transition text-gray-900"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-red-600 text-white font-bold py-3.5 px-6 rounded-lg shadow-md hover:bg-red-700 transition duration-200 flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {isSubmitting ? (
                    <>
                      <FaSpinner className="animate-spin" /> Sending Message...
                    </>
                  ) : (
                    <>
                      <FaPaperPlane /> Send Message
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="text-center py-14 px-4 max-w-5xl mx-auto">
          <span className="text-red-600 font-semibold uppercase tracking-wider text-sm border-b-2 border-red-600 pb-1">
            FAQ
          </span>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mt-4 mb-8">Frequently Asked Questions</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <FAQ
              question="What services do you offer?"
              answer="We specialize in custom web development, mobile app development, UI/UX graphic design, 2D/3D video animation, and digital marketing."
            />
            <FAQ
              question="How do I get a project quote?"
              answer="You can fill out our Project Requirements form on this website or send us a message with your project scope to receive a tailored estimate."
            />
            <FAQ
              question="What is your typical turnaround?"
              answer="Timelines depend on project complexity: standard websites take 1-3 weeks, while comprehensive web & mobile apps typically range from 4-8 weeks."
            />
          </div>
        </section>
      </div>
      <ToastContainer position="bottom-right" autoClose={4000} />
    </>
  );
};

function ContactMethod({ icon, title, content, subContent, children }) {
  return (
    <div className="p-6 rounded-xl bg-white border border-gray-200 shadow-md hover:shadow-lg transition-all text-center flex flex-col items-center justify-between">
      <div className="w-full flex flex-col items-center">
        <div className="w-12 h-12 rounded-full bg-red-50 text-red-600 flex items-center justify-center mb-3">
          {icon}
        </div>
        <h3 className="text-lg text-gray-900 font-bold mb-1">{title}</h3>
        {children ? (
          children
        ) : (
          <>
            <p className="text-gray-700 mt-2 font-medium text-sm">{content}</p>
            {subContent && <p className="text-gray-500 text-xs mt-1">{subContent}</p>}
          </>
        )}
      </div>
    </div>
  );
}

function FAQ({ question, answer }) {
  return (
    <div className="p-6 border border-gray-200 bg-white rounded-xl shadow-sm text-left hover:border-red-400 transition">
      <h3 className="text-base font-bold text-gray-900">{question}</h3>
      <p className="mt-2 text-sm text-gray-600 leading-relaxed">{answer}</p>
    </div>
  );
}

export default ContactUs;